
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  SmartToy as SmartToyIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AppService from "../services/AppService";
import { ImplementationService } from "../services/ImplementationService";
import type { App } from "../types/app";
import type {
  AppConstantsInputProfile,
  CompetitorScreenshotFeature,
  CompetitorScreenshotInput,
  FeatureDetail,
  ImplementationPlan,
} from "../types/implementation";

const MODELS = [
  { value: "", label: "Default (Gemini 2.5 Flash Lite)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
];

const STEPS = ["App Context", "Screenshot Analysis", "Constants Profile", "Review & Generate"];

const AI_FIELDS: Array<{ key: keyof AppConstantsInputProfile; label: string }> = [
  { key: "tagline", label: "Tagline" },
  { key: "supportEmail", label: "Support Email" },
  { key: "authMode", label: "Auth Mode" },
  { key: "activeAppStyle", label: "App Style" },
  { key: "bottomNavStyle", label: "Bottom Nav Style" },
  { key: "monetizationStrategy", label: "Monetization Strategy" },
  { key: "notificationDataSource", label: "Notification Source" },
  { key: "profileAuthLinkMode", label: "Auth Link Mode" },
  { key: "revenueCatEntitlementId", label: "Entitlement ID" },
  { key: "aiProvidersEnabled", label: "AI Providers Enabled" },
  { key: "aiTextGeneration", label: "Text Generation Provider" },
  { key: "aiImageGeneration", label: "Image Generation Provider" },
  { key: "aiVideoGeneration", label: "Video Generation Provider" },
];

const RECOMMENDED_FIELDS: Array<keyof AppConstantsInputProfile> = [
  "tagline",
  "supportEmail",
  "activeAppStyle",
  "bottomNavStyle",
  "monetizationStrategy",
  "notificationDataSource",
  "profileAuthLinkMode",
  "aiTextGeneration",
  "aiImageGeneration",
  "aiVideoGeneration",
];

type UploadedScreenshot = { id: string; name: string; mimeType: string; dataUrl: string };
type TabPanelProps = { children?: React.ReactNode; index: number; value: number };
type DraftPayload = {
  selectedModel: string;
  firebaseConfigSnippet: string;
  constantsProfile: AppConstantsInputProfile | null;
  uploadedScreenshots: UploadedScreenshot[];
  screenshotDraftFeatures: CompetitorScreenshotFeature[];
  aiFieldSelection: Record<string, boolean>;
  savedAt: string;
};

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>{value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null}</div>
);

const firstSentence = (value: string) => String(value || "").split(/[.!?]/).map((i) => i.trim()).find(Boolean) || "";
const formatDateTime = (d?: Date | null) => (d ? d.toLocaleString() : "-");
const resolveRegisteredStoreUrls = (app: App) => {
  const storeUrl = String(app.storeUrl || "").trim();
  const androidStoreUrl = String(app.androidStoreUrl || "").trim();
  const iosStoreUrl = String(app.iosStoreUrl || "").trim();
  return {
    storeUrl,
    androidStoreUrl,
    iosStoreUrl,
    appStoreUrl: iosStoreUrl || storeUrl,
    playStoreUrl: androidStoreUrl || storeUrl,
  };
};

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function compressImageFile(
  file: File,
): Promise<{ dataUrl: string; mimeType: string }> {
  const sourceDataUrl = await fileToDataUrl(file);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to decode image: ${file.name}`));
    img.src = sourceDataUrl;
  });

  const maxWidth = 1080;
  const maxHeight = 1920;
  const ratio = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
  const targetWidth = Math.max(1, Math.round(image.width * ratio));
  const targetHeight = Math.max(1, Math.round(image.height * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext("2d");
  if (!context) {
    return { dataUrl: sourceDataUrl, mimeType: file.type || "image/png" };
  }
  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  let quality = 0.82;
  let compressed = canvas.toDataURL("image/jpeg", quality);
  while (compressed.length > 650_000 && quality > 0.55) {
    quality -= 0.08;
    compressed = canvas.toDataURL("image/jpeg", quality);
  }
  return { dataUrl: compressed, mimeType: "image/jpeg" };
}

const ImplementationExpander: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [step, setStep] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [plans, setPlans] = useState<ImplementationPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<ImplementationPlan | null>(null);
  const [constantsProfile, setConstantsProfile] = useState<AppConstantsInputProfile | null>(null);
  const [uploadedScreenshots, setUploadedScreenshots] = useState<UploadedScreenshot[]>([]);
  const [screenshotDraftFeatures, setScreenshotDraftFeatures] = useState<CompetitorScreenshotFeature[]>([]);
  const [firebaseConfigSnippet, setFirebaseConfigSnippet] = useState("");
  const [aiFieldSelection, setAiFieldSelection] = useState<Record<keyof AppConstantsInputProfile, boolean>>({} as Record<keyof AppConstantsInputProfile, boolean>);
  const [aiPendingUpdates, setAiPendingUpdates] = useState<Partial<AppConstantsInputProfile> | null>(null);
  const [aiDiffOpen, setAiDiffOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiFillingFields, setAiFillingFields] = useState(false);
  const [analyzingScreenshots, setAnalyzingScreenshots] = useState(false);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<Date | null>(null);
  const [draftWarning, setDraftWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedApp = useMemo(() => apps.find((app) => app.appId === selectedAppId) || null, [apps, selectedAppId]);
  const draftStorageKey = useMemo(() => (selectedAppId ? `implementation_plan_draft_${selectedAppId}` : ""), [selectedAppId]);
  const selectedFieldKeys = useMemo(
    () => Object.keys(aiFieldSelection).filter((k) => aiFieldSelection[k as keyof AppConstantsInputProfile]) as Array<keyof AppConstantsInputProfile>,
    [aiFieldSelection],
  );

  const readiness = useMemo(() => {
    const checks = [Boolean(selectedAppId), Boolean(constantsProfile?.appId), Boolean(constantsProfile?.appNameDisplay), Boolean(constantsProfile?.tagline), Boolean(constantsProfile?.version), Boolean(constantsProfile?.notificationDataSource?.length)];
    const complete = checks.filter(Boolean).length;
    return { complete, total: checks.length, percent: Math.round((complete / checks.length) * 100) };
  }, [selectedAppId, constantsProfile]);

  const workloadHint = useMemo(() => {
    const count = uploadedScreenshots.length;
    const modelLabel = MODELS.find((m) => m.value === selectedModel)?.label || "Default model";
    if (count >= 6) return `High analysis workload: ${count} screenshots on ${modelLabel}.`;
    if (count >= 3) return `Moderate analysis workload: ${count} screenshots on ${modelLabel}.`;
    return `Low analysis workload: ${count} screenshot(s) on ${modelLabel}.`;
  }, [uploadedScreenshots.length, selectedModel]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    setApps(await AppService.getUserApps(user.email));
  }, [user?.email]);

  const loadPlans = useCallback(async () => {
    if (!user?.email) return;
    setPlans(await ImplementationService.getUserPlans(user.email, selectedAppId || undefined));
  }, [selectedAppId, user?.email]);

  useEffect(() => { loadApps().catch((e) => { console.error(e); setError("Failed to load registered apps."); }); }, [loadApps]);
  useEffect(() => { loadPlans().catch((e) => { console.error(e); setError("Failed to load implementation plans."); }); }, [loadPlans]);

  useEffect(() => {
    if (!selectedApp) {
      setConstantsProfile(null);
      setUploadedScreenshots([]);
      setScreenshotDraftFeatures([]);
      setFirebaseConfigSnippet("");
      setSelectedModel("");
      setIsDraftLoaded(false);
      return;
    }
    const registeredStoreUrls = resolveRegisteredStoreUrls(selectedApp);
    const base = ImplementationService.defaultAppConstantsProfile({
      appId: selectedApp.googlePlayPackageName || selectedApp.appId,
      appName: selectedApp.name,
      description: selectedApp.description || "",
      category: selectedApp.category || "productivity",
      storeUrl: registeredStoreUrls.storeUrl,
      androidStoreUrl: registeredStoreUrls.androidStoreUrl,
      iosStoreUrl: registeredStoreUrls.iosStoreUrl,
    });
    const profileFromApp: AppConstantsInputProfile = {
      ...base,
      appNameNative: selectedApp.name || base.appNameNative,
      appNameDisplay: selectedApp.name || base.appNameDisplay,
      tagline: firstSentence(selectedApp.description) || base.tagline,
      privacyUrl: selectedApp.privacyPolicyUrl || "",
      termsUrl: selectedApp.termsOfServiceUrl || "",
      appStoreUrl: registeredStoreUrls.appStoreUrl,
      playStoreUrl: registeredStoreUrls.playStoreUrl,
      appId: selectedApp.googlePlayPackageName || selectedApp.appId,
    };
    setConstantsProfile(profileFromApp);
    setUploadedScreenshots([]);
    setScreenshotDraftFeatures([]);
    setFirebaseConfigSnippet("");
    setSelectedModel("");
    setAiFieldSelection({} as Record<keyof AppConstantsInputProfile, boolean>);
    setStep(0);
    setLastDraftSavedAt(null);
    setDraftWarning(null);

    if (!draftStorageKey) {
      setIsDraftLoaded(true);
      return;
    }
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) {
        setIsDraftLoaded(true);
        return;
      }
      const draft = JSON.parse(raw) as DraftPayload;
      setSelectedModel(draft.selectedModel || "");
      setFirebaseConfigSnippet(draft.firebaseConfigSnippet || "");
      const restoredProfile = draft.constantsProfile || profileFromApp;
      setConstantsProfile({
        ...restoredProfile,
        appStoreUrl: registeredStoreUrls.appStoreUrl,
        playStoreUrl: registeredStoreUrls.playStoreUrl,
      });
      setUploadedScreenshots(Array.isArray(draft.uploadedScreenshots) ? draft.uploadedScreenshots : []);
      setScreenshotDraftFeatures(Array.isArray(draft.screenshotDraftFeatures) ? draft.screenshotDraftFeatures : []);
      setAiFieldSelection((draft.aiFieldSelection || {}) as Record<keyof AppConstantsInputProfile, boolean>);
      setLastDraftSavedAt(draft.savedAt ? new Date(draft.savedAt) : null);
      setSuccess("Draft restored for this app.");
    } catch (e) {
      console.error(e);
      setDraftWarning("Draft exists but could not be restored.");
    } finally {
      setIsDraftLoaded(true);
    }
  }, [selectedApp, draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey || !isDraftLoaded || !selectedAppId || !constantsProfile) return;
    const timer = window.setTimeout(() => {
      const payload: DraftPayload = {
        selectedModel,
        firebaseConfigSnippet,
        constantsProfile,
        uploadedScreenshots,
        screenshotDraftFeatures,
        aiFieldSelection: aiFieldSelection as Record<string, boolean>,
        savedAt: new Date().toISOString(),
      };
      try {
        localStorage.setItem(draftStorageKey, JSON.stringify(payload));
        setLastDraftSavedAt(new Date());
      } catch {
        try {
          localStorage.setItem(draftStorageKey, JSON.stringify({ ...payload, uploadedScreenshots: [] }));
          setLastDraftSavedAt(new Date());
          setDraftWarning("Large screenshots were excluded from draft due to browser storage limits.");
        } catch {
          setDraftWarning("Draft could not be persisted due to storage limits.");
        }
      }
    }, 500);
    return () => window.clearTimeout(timer);
  }, [draftStorageKey, isDraftLoaded, selectedAppId, selectedModel, firebaseConfigSnippet, constantsProfile, uploadedScreenshots, screenshotDraftFeatures, aiFieldSelection]);

  const updateProfile = <K extends keyof AppConstantsInputProfile>(key: K, value: AppConstantsInputProfile[K]) =>
    setConstantsProfile((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleScreenshotUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setError(null);
    try {
      const converted = await Promise.all(files.map(async (file) => ({
        ...((await compressImageFile(file)) || {}),
        id: `${Date.now()}_${file.name}_${Math.random().toString(36).slice(2, 6)}`,
        name: file.name,
        mimeType: "image/jpeg",
      })));
      setUploadedScreenshots((prev) => [...prev, ...converted]);
      setSuccess(`${converted.length} screenshot(s) added.`);
    } catch (e: any) {
      setError(e?.message || "Failed to upload screenshots.");
    } finally {
      event.target.value = "";
    }
  };

  const handleAnalyzeScreenshots = async () => {
    if (!selectedApp) return setError("Select a registered app first.");
    if (!uploadedScreenshots.length) return setError("Upload at least one screenshot to analyze.");
    setAnalyzingScreenshots(true);
    setError(null);
    try {
      const features = await ImplementationService.analyzeCompetitorScreenshots({
        appName: selectedApp.name,
        description: selectedApp.description || "",
        category: selectedApp.category || "productivity",
        platform: selectedApp.platform,
        storeUrl: selectedApp.storeUrl,
        androidStoreUrl: selectedApp.androidStoreUrl,
        iosStoreUrl: selectedApp.iosStoreUrl,
        researchContext: selectedApp.researchContext,
        model: selectedModel || undefined,
        competitorScreenshots: uploadedScreenshots.map((item) => ({ name: item.name, mimeType: item.mimeType, dataUrl: item.dataUrl })),
      });
      setScreenshotDraftFeatures(features);
      setSuccess(`Analyzed ${uploadedScreenshots.length} screenshot(s).`);
    } catch (e: any) {
      setError(e?.message || "Failed to analyze screenshots.");
    } finally {
      setAnalyzingScreenshots(false);
    }
  };

  const handleFillSelectedWithAI = async () => {
    if (!selectedApp || !constantsProfile) return setError("Select a registered app first.");
    if (!selectedFieldKeys.length) return setError("Select at least one field to generate with AI.");
    const registeredStoreUrls = resolveRegisteredStoreUrls(selectedApp);
    const profileWithRegisteredStoreUrls: AppConstantsInputProfile = {
      ...constantsProfile,
      appStoreUrl: registeredStoreUrls.appStoreUrl,
      playStoreUrl: registeredStoreUrls.playStoreUrl,
    };
    setAiFillingFields(true);
    setError(null);
    try {
      const updates = await ImplementationService.generateConfigFieldSuggestions({
        appId: selectedApp.appId,
        appName: selectedApp.name,
        description: selectedApp.description || "",
        category: selectedApp.category || "productivity",
        platform: selectedApp.platform,
        storeUrl: registeredStoreUrls.storeUrl,
        androidStoreUrl: registeredStoreUrls.androidStoreUrl,
        iosStoreUrl: registeredStoreUrls.iosStoreUrl,
        researchContext: selectedApp.researchContext,
        model: selectedModel || undefined,
        existingProfile: profileWithRegisteredStoreUrls,
        selectedFields: selectedFieldKeys,
      });
      setAiPendingUpdates(updates);
      setAiDiffOpen(true);
    } catch (e: any) {
      setError(e?.message || "Failed to generate AI field suggestions.");
    } finally {
      setAiFillingFields(false);
    }
  };

  const applyPendingAIDiff = () => {
    if (!constantsProfile || !aiPendingUpdates) return;
    setConstantsProfile({ ...constantsProfile, ...aiPendingUpdates } as AppConstantsInputProfile);
    setAiPendingUpdates(null);
    setAiDiffOpen(false);
    setSuccess("AI suggestions applied.");
  };

  const handleGenerate = async () => {
    if (!user?.email || !selectedApp || !constantsProfile) return setError("Select a registered app first.");
    const registeredStoreUrls = resolveRegisteredStoreUrls(selectedApp);
    const profileWithRegisteredStoreUrls: AppConstantsInputProfile = {
      ...constantsProfile,
      appStoreUrl: registeredStoreUrls.appStoreUrl,
      playStoreUrl: registeredStoreUrls.playStoreUrl,
    };
    setConstantsProfile(profileWithRegisteredStoreUrls);
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const competitorScreenshots: CompetitorScreenshotInput[] = uploadedScreenshots.map((item) => ({ name: item.name, mimeType: item.mimeType, dataUrl: item.dataUrl }));
      const generated = await ImplementationService.generatePlan(user.email, {
        appId: selectedApp.appId,
        appName: selectedApp.name,
        description: selectedApp.description || "",
        category: selectedApp.category || "productivity",
        platform: selectedApp.platform,
        storeUrl: registeredStoreUrls.storeUrl,
        androidStoreUrl: registeredStoreUrls.androidStoreUrl,
        iosStoreUrl: registeredStoreUrls.iosStoreUrl,
        researchContext: selectedApp.researchContext,
        model: selectedModel || undefined,
        appConstantsProfile: profileWithRegisteredStoreUrls,
        competitorScreenshots,
        competitorScreenshotFeatureDraft: screenshotDraftFeatures,
        firebaseConfigSnippet: firebaseConfigSnippet.trim() || undefined,
      });
      await loadPlans();
      setSelectedPlan(generated);
      setTab(2);
      setSuccess("Implementation plan generated.");
    } catch (e: any) {
      setError(e?.message || "Failed to generate implementation plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (plan: ImplementationPlan) => {
    if (!window.confirm("Delete this implementation plan?")) return;
    setLoading(true);
    try {
      await ImplementationService.deletePlan(plan.planId);
      await loadPlans();
      if (selectedPlan?.planId === plan.planId) setSelectedPlan(null);
      setSuccess("Implementation plan deleted.");
    } catch (e: any) {
      setError(e?.message || "Failed to delete plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async (plan: ImplementationPlan) => {
    setSaving(true);
    setError(null);
    try {
      await ImplementationService.savePlan(plan.planId);
      const now = new Date();
      setPlans((prev) => prev.map((item) => item.planId === plan.planId ? { ...item, savedAt: now, updatedAt: now } : item));
      if (selectedPlan?.planId === plan.planId) setSelectedPlan((prev) => (prev ? { ...prev, savedAt: now, updatedAt: now } : prev));
      setSuccess("Implementation plan saved.");
    } catch (e: any) {
      setError(e?.message || "Failed to save implementation plan.");
    } finally {
      setSaving(false);
    }
  };

  const exportPlan = (plan: ImplementationPlan, format: "md" | "json") => {
    const content = format === "md" ? ImplementationService.exportAsMarkdown(plan) : ImplementationService.exportAsJSON(plan);
    const element = document.createElement("a");
    element.setAttribute("href", `data:${format === "md" ? "text/markdown" : "application/json"};charset=utf-8,${encodeURIComponent(content)}`);
    element.setAttribute("download", `${plan.appName}-implementation-plan-generator.${format}`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setSuccess(`${label} copied.`);
    } catch {
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const canGoNext = step === 0 ? Boolean(selectedApp) : step === 2 ? Boolean(constantsProfile) : true;

  const renderStepBody = () => {
    if (step === 0) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField select fullWidth label="Registered App" value={selectedAppId} onChange={(e) => setSelectedAppId(e.target.value)}><MenuItem value=""><em>Choose your app</em></MenuItem>{apps.map((app) => <MenuItem key={app.appId} value={app.appId}>{app.name}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} md={6}><TextField select fullWidth label="Model" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>{MODELS.map((m) => <MenuItem key={m.value || "default"} value={m.value}>{m.label}</MenuItem>)}</TextField></Grid>
          {selectedApp && <Grid item xs={12}><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Context Snapshot</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>{selectedApp.description || "No app description available."}</Typography><Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}><Chip size="small" label={`Platform: ${selectedApp.platform.toUpperCase()}`} /><Chip size="small" label={`Category: ${selectedApp.category || "N/A"}`} /></Box></Paper></Grid>}
        </Grid>
      );
    }

    if (step === 1) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}><Button component="label" variant="outlined" startIcon={<UploadIcon />} sx={{ mb: 1 }}>Upload Screenshots<input hidden multiple accept="image/*" type="file" onChange={handleScreenshotUpload} /></Button><Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{uploadedScreenshots.map((item, index) => <Paper key={item.id} variant="outlined" sx={{ p: 1, width: 132 }}><Box component="img" src={item.dataUrl} alt={item.name} sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 1 }} /><Typography variant="caption" display="block">#{index + 1} {item.name}</Typography><Button size="small" color="error" onClick={() => setUploadedScreenshots((prev) => prev.filter((x) => x.id !== item.id))}>Remove</Button></Paper>)}</Box></Grid>
          <Grid item xs={12}><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Analyze Before Generate</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Generate and edit screenshot feature breakdown now.</Typography><Button variant="outlined" startIcon={<SearchIcon />} onClick={handleAnalyzeScreenshots} disabled={analyzingScreenshots || !uploadedScreenshots.length}>{analyzingScreenshots ? "Analyzing..." : "Analyze Screenshots"}</Button></Paper></Grid>
          <Grid item xs={12}>{!screenshotDraftFeatures.length ? <Alert severity="info">Run screenshot analysis to review extracted features here.</Alert> : <Box sx={{ display: "grid", gap: 1 }}>{screenshotDraftFeatures.map((feature, index) => <Accordion key={`${feature.featureName}_${index}`} defaultExpanded={index === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{index + 1}. {feature.featureName || "Feature"}</Typography></AccordionSummary><AccordionDetails><Grid container spacing={1}><Grid item xs={12} md={6}><TextField fullWidth label="Screenshot Label" value={feature.screenshotLabel} onChange={(e) => setScreenshotDraftFeatures((prev) => prev.map((x, i) => i === index ? { ...x, screenshotLabel: e.target.value } : x))} /></Grid><Grid item xs={12} md={6}><TextField fullWidth label="Feature Name" value={feature.featureName} onChange={(e) => setScreenshotDraftFeatures((prev) => prev.map((x, i) => i === index ? { ...x, featureName: e.target.value } : x))} /></Grid><Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Feature Details" value={feature.featureDetails} onChange={(e) => setScreenshotDraftFeatures((prev) => prev.map((x, i) => i === index ? { ...x, featureDetails: e.target.value } : x))} /></Grid><Grid item xs={12}><TextField fullWidth multiline minRows={2} label="User Flow" value={feature.userFlow} onChange={(e) => setScreenshotDraftFeatures((prev) => prev.map((x, i) => i === index ? { ...x, userFlow: e.target.value } : x))} /></Grid><Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Implementation Notes" value={feature.implementationNotes} onChange={(e) => setScreenshotDraftFeatures((prev) => prev.map((x, i) => i === index ? { ...x, implementationNotes: e.target.value } : x))} /></Grid></Grid></AccordionDetails></Accordion>)}</Box>}</Grid>
        </Grid>
      );
    }

    if (step === 2 && constantsProfile) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><Card variant="outlined"><CardContent><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Identity</Typography><Grid container spacing={1}><Grid item xs={12}><TextField fullWidth label="APP_ID" value={constantsProfile.appId} onChange={(e) => updateProfile("appId", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Display Name" value={constantsProfile.appNameDisplay} onChange={(e) => updateProfile("appNameDisplay", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Native Name" value={constantsProfile.appNameNative} onChange={(e) => updateProfile("appNameNative", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Tagline" value={constantsProfile.tagline} onChange={(e) => updateProfile("tagline", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Version" value={constantsProfile.version} onChange={(e) => updateProfile("version", e.target.value)} /></Grid></Grid></CardContent></Card></Grid>
          <Grid item xs={12} md={6}><Card variant="outlined"><CardContent><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Platform & Store Details</Typography><Grid container spacing={1}><Grid item xs={12}><TextField fullWidth label="App Store URL" value={constantsProfile.appStoreUrl} onChange={(e) => updateProfile("appStoreUrl", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Play Store URL" value={constantsProfile.playStoreUrl} onChange={(e) => updateProfile("playStoreUrl", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Support Email" value={constantsProfile.supportEmail} onChange={(e) => updateProfile("supportEmail", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Privacy URL" value={constantsProfile.privacyUrl} onChange={(e) => updateProfile("privacyUrl", e.target.value)} /></Grid><Grid item xs={12}><TextField fullWidth label="Terms URL" value={constantsProfile.termsUrl} onChange={(e) => updateProfile("termsUrl", e.target.value)} /></Grid></Grid></CardContent></Card></Grid>
          <Grid item xs={12}><Card variant="outlined"><CardContent><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Product Configuration</Typography><Grid container spacing={1}><Grid item xs={12} md={4}><TextField select fullWidth label="Auth Mode" value={constantsProfile.authMode} onChange={(e) => updateProfile("authMode", e.target.value as AppConstantsInputProfile["authMode"])}><MenuItem value="firebase">firebase</MenuItem><MenuItem value="frictionless_device_id">frictionless_device_id</MenuItem></TextField></Grid><Grid item xs={12} md={4}><TextField select fullWidth label="App Style" value={constantsProfile.activeAppStyle} onChange={(e) => updateProfile("activeAppStyle", e.target.value as AppConstantsInputProfile["activeAppStyle"])}>{["productivity","fitness","finance","wellness","education","travel","food","ecommerce","social","ai"].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid><Grid item xs={12} md={4}><TextField select fullWidth label="Bottom Nav Style" value={constantsProfile.bottomNavStyle} onChange={(e) => updateProfile("bottomNavStyle", e.target.value as AppConstantsInputProfile["bottomNavStyle"])}>{["floating_fab","pill","minimal_line","bubble_dock","segmented"].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}</TextField></Grid><Grid item xs={12} md={4}><TextField select fullWidth label="Monetization" value={constantsProfile.monetizationStrategy} onChange={(e) => updateProfile("monetizationStrategy", e.target.value as AppConstantsInputProfile["monetizationStrategy"])}><MenuItem value="subscription_2tier">subscription_2tier</MenuItem><MenuItem value="subscription_3tier">subscription_3tier</MenuItem><MenuItem value="iap_only">iap_only</MenuItem></TextField></Grid><Grid item xs={12} md={4}><TextField select fullWidth label="Profile Auth Link" value={constantsProfile.profileAuthLinkMode} onChange={(e) => updateProfile("profileAuthLinkMode", e.target.value as AppConstantsInputProfile["profileAuthLinkMode"])}><MenuItem value="signup">signup</MenuItem><MenuItem value="login">login</MenuItem></TextField></Grid><Grid item xs={12} md={4}><TextField fullWidth label="Entitlement ID" value={constantsProfile.revenueCatEntitlementId} onChange={(e) => updateProfile("revenueCatEntitlementId", e.target.value)} /></Grid><Grid item xs={12}><Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Notification Source (choose one or both)</Typography><Box sx={{ display: "flex", gap: 1 }}>{["firestore","local"].map((value) => { const selected = constantsProfile.notificationDataSource.includes(value as "firestore" | "local"); return <Chip key={value} label={value} color={selected ? "primary" : "default"} variant={selected ? "filled" : "outlined"} onClick={() => { const current = constantsProfile.notificationDataSource; const next = selected ? current.filter((x) => x !== value) : [...current, value as "firestore" | "local"]; updateProfile("notificationDataSource", next.length ? next : ["firestore"]); }} />; })}</Box></Grid></Grid></CardContent></Card></Grid>
          <Grid item xs={12}><Card variant="outlined"><CardContent><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>AI App Provider Selection (Optional)</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Configure provider:model per workload. Remote Config keys: ai_text_generation, ai_image_generation, ai_video_generation.</Typography><Grid container spacing={1}><Grid item xs={12} md={3}><TextField select fullWidth label="AI Providers Enabled" value={String(constantsProfile.aiProvidersEnabled)} onChange={(e) => updateProfile("aiProvidersEnabled", e.target.value === "true")}><MenuItem value="true">true</MenuItem><MenuItem value="false">false</MenuItem></TextField></Grid><Grid item xs={12} md={3}><TextField select fullWidth label="Text Generation" value={constantsProfile.aiTextGeneration} onChange={(e) => updateProfile("aiTextGeneration", e.target.value)}><MenuItem value="Gemini:gemini-2.5-flash-lite">Gemini:gemini-2.5-flash-lite</MenuItem><MenuItem value="Gemini:gemini-2.5-flash">Gemini:gemini-2.5-flash</MenuItem><MenuItem value="OpenAI:gpt-4.1-mini">OpenAI:gpt-4.1-mini</MenuItem><MenuItem value="OpenAI:gpt-4.1">OpenAI:gpt-4.1</MenuItem></TextField></Grid><Grid item xs={12} md={3}><TextField select fullWidth label="Image Generation" value={constantsProfile.aiImageGeneration} onChange={(e) => updateProfile("aiImageGeneration", e.target.value)}><MenuItem value="OpenAI:gpt-image-2.5-flare">OpenAI:gpt-image-2.5-flare</MenuItem><MenuItem value="OpenAI:gpt-image-2.5-sunburst">OpenAI:gpt-image-2.5-sunburst</MenuItem><MenuItem value="Kling:kling-image-v1">Kling:kling-image-v1</MenuItem><MenuItem value="Gemini:gemini-2.5-flash-image">Gemini:gemini-2.5-flash-image</MenuItem></TextField></Grid><Grid item xs={12} md={3}><TextField select fullWidth label="Video Generation" value={constantsProfile.aiVideoGeneration} onChange={(e) => updateProfile("aiVideoGeneration", e.target.value)}><MenuItem value="Kling:kling-v2.6-std">Kling:kling-v2.6-std</MenuItem><MenuItem value="Kling:kling-v2.6-pro">Kling:kling-v2.6-pro</MenuItem><MenuItem value="Google:veo-3.1-fast">Google:veo-3.1-fast</MenuItem><MenuItem value="Google:veo-3.1-generate-preview">Google:veo-3.1-generate-preview</MenuItem></TextField></Grid><Grid item xs={12}><Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}><Typography variant="caption" color="text.secondary" display="block">Optional JSON block preview</Typography><Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>{JSON.stringify({ aiProviders: { enabled: constantsProfile.aiProvidersEnabled, textGeneration: constantsProfile.aiTextGeneration, imageGeneration: constantsProfile.aiImageGeneration, videoGeneration: constantsProfile.aiVideoGeneration } }, null, 2)}</Typography></Paper></Grid></Grid></CardContent></Card></Grid>
          <Grid item xs={12}><Accordion><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>AI Fill</Typography></AccordionSummary><AccordionDetails><Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}><Button size="small" variant="text" onClick={() => setAiFieldSelection((prev) => { const next = { ...prev }; RECOMMENDED_FIELDS.forEach((f) => { next[f] = true; }); return next; })}>Select Recommended Fields</Button>{AI_FIELDS.map((field) => <Chip key={field.key} label={field.label} clickable color={aiFieldSelection[field.key] ? "primary" : "default"} variant={aiFieldSelection[field.key] ? "filled" : "outlined"} onClick={() => setAiFieldSelection((prev) => ({ ...prev, [field.key]: !prev[field.key] }))} />)}</Box><Button variant="outlined" startIcon={<SmartToyIcon />} disabled={aiFillingFields || !selectedFieldKeys.length} onClick={handleFillSelectedWithAI}>{aiFillingFields ? "Generating..." : "Generate AI Suggestions"}</Button></AccordionDetails></Accordion></Grid>
          <Grid item xs={12}><TextField fullWidth label="Firebase Config Object (Optional)" placeholder="const firebaseConfig = { ... }" multiline minRows={7} value={firebaseConfigSnippet} onChange={(e) => setFirebaseConfigSnippet(e.target.value)} helperText="Appended to the bottom of the generated implementation plan document." /></Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Review Checklist</Typography><Typography variant="body2" sx={{ mt: 1 }}>App selected: {selectedApp ? "Yes" : "No"}</Typography><Typography variant="body2">Screenshots attached: {uploadedScreenshots.length}</Typography><Typography variant="body2">Screenshot features reviewed: {screenshotDraftFeatures.length}</Typography><Typography variant="body2">Constants profile readiness: {readiness.complete}/{readiness.total}</Typography><Typography variant="body2">Draft saved: {formatDateTime(lastDraftSavedAt)}</Typography></Paper></Grid>
        <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Generation Notes</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{workloadHint}</Typography></Paper></Grid>
        <Grid item xs={12}><Button variant="contained" startIcon={<SearchIcon />} onClick={handleGenerate} disabled={loading || !selectedApp || !constantsProfile}>{loading ? "Generating..." : "Generate Implementation Plan"}</Button></Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>Implementation Plan Generator</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>Build implementation-ready plans from app context, screenshot analysis, and reviewed constants.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {draftWarning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDraftWarning(null)}>{draftWarning}</Alert>}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }}>
        <Tab label="Create Plan" />
        <Tab label="Plan Library" />
        <Tab label="Plan Details" />
      </Tabs>

      <TabPanel value={tab} index={0}><Paper sx={{ p: 2.5, pb: 10 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Readiness {readiness.percent}%</Typography><LinearProgress variant="determinate" value={readiness.percent} sx={{ mb: 2 }} /><Stepper activeStep={step} alternativeLabel sx={{ mb: 2 }}>{STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>{renderStepBody()}<Box sx={{ position: "sticky", bottom: 0, zIndex: 3, mt: 2, p: 1.2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper", boxShadow: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}><Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{workloadHint}</Typography><Typography variant="caption" color="text.secondary">Draft last saved: {formatDateTime(lastDraftSavedAt)}</Typography></Box><Box sx={{ display: "flex", gap: 1 }}><Button disabled={step === 0} onClick={() => setStep((p) => Math.max(0, p - 1))}>Back</Button>{step < STEPS.length - 1 ? <Button variant="contained" disabled={!canGoNext} onClick={() => setStep((p) => Math.min(STEPS.length - 1, p + 1))}>Next</Button> : <Button variant="contained" startIcon={<SearchIcon />} onClick={handleGenerate} disabled={loading || !selectedApp || !constantsProfile}>{loading ? "Generating..." : "Generate Implementation Plan"}</Button>}</Box></Box></Paper></TabPanel>

      <TabPanel value={tab} index={1}>
        {loading ? <Typography>Loading...</Typography> : plans.length === 0 ? <Alert severity="info">No implementation plans yet for the selected app.</Alert> : (
          <Grid container spacing={2}>{plans.map((plan) => (
            <Grid item xs={12} md={6} key={plan.planId}>
              <Card variant="outlined"><CardContent><Typography variant="h6">{plan.appName}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>{plan.summary}</Typography><Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Model: {plan.selectedModel} | Screenshot features: {plan.competitorScreenshotFeatures?.length || 0}</Typography><Typography variant="caption" color="text.secondary" display="block">Created: {formatDateTime(plan.createdAt)}</Typography></CardContent><CardActions><Button size="small" onClick={() => { setSelectedPlan(plan); setTab(2); }}>Open</Button><Button size="small" startIcon={<SaveIcon />} onClick={() => handleSavePlan(plan)} disabled={saving}>Save</Button><Button size="small" startIcon={<DownloadIcon />} onClick={() => exportPlan(plan, "json")}>JSON</Button><Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(plan)}>Delete</Button></CardActions></Card>
            </Grid>
          ))}</Grid>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {!selectedPlan ? <Alert severity="info">Select a plan to view details.</Alert> : (
          <Paper sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}><Typography variant="h5" sx={{ fontWeight: 700 }}>{selectedPlan.appName} Implementation Plan</Typography><Box sx={{ display: "flex", gap: 1 }}><Button size="small" startIcon={<SaveIcon />} onClick={() => handleSavePlan(selectedPlan)} disabled={saving}>{saving ? "Saving..." : "Save"}</Button><Button size="small" startIcon={<DownloadIcon />} onClick={() => exportPlan(selectedPlan, "md")}>Markdown</Button><Button size="small" startIcon={<DownloadIcon />} onClick={() => exportPlan(selectedPlan, "json")}>JSON</Button></Box></Box>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Table of Contents</Typography><Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>{[{ id: "section-summary", label: "Summary" }, { id: "section-core", label: "Core Feature" }, { id: "section-comp", label: "Complementary Features" }, { id: "section-constants", label: "App Constants" }, { id: "section-shots", label: "Screenshot Features" }, { id: "section-prompts", label: "Starter Prompt & Config" }].map((a) => <Chip key={a.id} label={a.label} variant="outlined" onClick={() => document.getElementById(a.id)?.scrollIntoView({ behavior: "smooth", block: "start" })} />)}</Box></Paper>
            <Box id="section-summary"><DetailRow label="Selected Model" value={selectedPlan.selectedModel} /><DetailRow label="Suggested Best App Name" value={selectedPlan.suggestedBestAppName} /><DetailRow label="Summary" value={selectedPlan.summary} /></Box>
            <Divider sx={{ my: 1.5 }} />
            <Box id="section-core"><FeatureBlock label="Core Feature" feature={selectedPlan.coreFeature} /></Box>
            <Divider sx={{ my: 1.5 }} />
            <Box id="section-comp"><FeatureList label="5 Complementary Features" features={selectedPlan.complementaryFeatures} /></Box>
            <Divider sx={{ my: 1.5 }} />
            <Box id="section-constants">{selectedPlan.appConstantsProfile && <DetailKeyValueList label="App Constants Profile" values={selectedPlan.appConstantsProfile as unknown as Record<string, unknown>} />}</Box>
            <Divider sx={{ my: 1.5 }} />
            <Box id="section-shots"><ScreenshotFeatureList items={selectedPlan.competitorScreenshotFeatures || []} /></Box>
            <Divider sx={{ my: 1.5 }} />
            <Box id="section-prompts"><DetailMultiline label="Implementation Starter Prompt" value={selectedPlan.implementationStarterPrompt || "N/A"} onCopy={(value) => copyToClipboard(value, "Implementation Starter Prompt")} /><DetailMultiline label="Firebase Config" value={selectedPlan.firebaseConfigSnippet ? `This is firebase config of the project, use it where necessary.\n\n${selectedPlan.firebaseConfigSnippet}` : "N/A"} onCopy={(value) => copyToClipboard(value, "Firebase Config")} /><DetailMultiline label="Core Builder Tech Stack" value={selectedPlan.coreBuilderTechStack || "N/A"} onCopy={(value) => copyToClipboard(value, "Core Builder Tech Stack")} /></Box>
          </Paper>
        )}
      </TabPanel>

      <Dialog open={aiDiffOpen} onClose={() => setAiDiffOpen(false)} fullWidth maxWidth="md"><DialogTitle>AI Field Diff Preview</DialogTitle><DialogContent dividers>{!constantsProfile || !aiPendingUpdates ? <Typography variant="body2">No changes to preview.</Typography> : <Box sx={{ display: "grid", gap: 1.2 }}>{Object.keys(aiPendingUpdates).map((key) => { const typedKey = key as keyof AppConstantsInputProfile; const before = JSON.stringify(constantsProfile[typedKey]); const after = JSON.stringify(aiPendingUpdates[typedKey]); if (before === after) return null; return <Paper key={key} variant="outlined" sx={{ p: 1.2 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{key}</Typography><Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Current</Typography><Typography variant="body2">{before}</Typography><Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>AI Suggestion</Typography><Typography variant="body2">{after}</Typography></Paper>; })}</Box>}</DialogContent><DialogActions><Button onClick={() => setAiDiffOpen(false)}>Cancel</Button><Button variant="contained" onClick={applyPendingAIDiff}>Apply Suggestions</Button></DialogActions></Dialog>
    </Box>
  );
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return <Box sx={{ mb: 1.2 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography><Typography variant="body2">{value}</Typography></Box>;
}

function DetailKeyValueList({ label, values }: { label: string; values: Record<string, unknown> }) {
  return <Box sx={{ mb: 1.6 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography><Paper variant="outlined" sx={{ p: 1.5 }}>{Object.entries(values).map(([key, value]) => <Typography key={key} variant="body2"><strong>{key}:</strong> {String(value)}</Typography>)}</Paper></Box>;
}

function DetailMultiline({ label, value, onCopy }: { label: string; value: string; onCopy: (value: string) => void }) {
  const isLong = value.length > 700;
  return <Box sx={{ mb: 1.4 }}><Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography><IconButton size="small" onClick={() => onCopy(value)}><ContentCopyIcon fontSize="small" /></IconButton></Box>{isLong ? <Accordion><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2" color="text.secondary">Expand content</Typography></AccordionSummary><AccordionDetails><Paper variant="outlined" sx={{ p: 1.5, whiteSpace: "pre-wrap" }}><Typography variant="body2">{value}</Typography></Paper></AccordionDetails></Accordion> : <Paper variant="outlined" sx={{ p: 1.5, whiteSpace: "pre-wrap" }}><Typography variant="body2">{value}</Typography></Paper>}</Box>;
}

function ScreenshotFeatureList({ items }: { items: ImplementationPlan["competitorScreenshotFeatures"] }) {
  return <Box sx={{ mb: 1.8 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Competitor Screenshot Feature Breakdown</Typography>{!items || !items.length ? <Typography variant="body2">No screenshot feature analysis available.</Typography> : <Box sx={{ display: "grid", gap: 1 }}>{items.map((item, index) => <Accordion key={`${item.featureName}_${index}`} defaultExpanded={index === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{index + 1}. {item.screenshotLabel} - {item.featureName}</Typography></AccordionSummary><AccordionDetails><Typography variant="body2"><strong>Feature details:</strong> {item.featureDetails}</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><strong>User flow:</strong> {item.userFlow}</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><strong>Implementation notes:</strong> {item.implementationNotes}</Typography></AccordionDetails></Accordion>)}</Box>}</Box>;
}

function FeatureBlock({ label, feature }: { label: string; feature: FeatureDetail }) {
  return <Box sx={{ mb: 1.8 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{feature.name}</Typography><Typography variant="body2" sx={{ mt: 0.8 }}><strong>Details:</strong> {feature.details}</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><strong>How it works:</strong> {feature.howItWorks}</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><strong>Success looks like:</strong> {feature.successLooksLike}</Typography></Paper></Box>;
}

function FeatureList({ label, features }: { label: string; features: FeatureDetail[] }) {
  return <Box sx={{ mb: 1.8 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography><Box sx={{ display: "grid", gap: 1 }}>{features.map((feature, index) => <Paper key={`${feature.name}-${index}`} variant="outlined" sx={{ p: 1.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{index + 1}. {feature.name}</Typography><Typography variant="body2" sx={{ mt: 0.8 }}><strong>Details:</strong> {feature.details}</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><strong>How it works:</strong> {feature.howItWorks}</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><strong>Success looks like:</strong> {feature.successLooksLike}</Typography></Paper>)}</Box></Box>;
}

export default ImplementationExpander;
